import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Button } from '#app/components/buttons/button.tsx'
import { Flex } from '#app/components/layout/flex.tsx'
import { VisuallyHidden } from '#app/components/layout/visually-hidden.tsx'
import { Link } from '#app/components/navigation/link.tsx'
import { Popover, PopoverTitle, PopoverTrigger } from '#app/components/overlays/popover.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Span } from '#app/components/typography/span.tsx'
import { Code } from '#app/old_components/typography/code.tsx'
import { Icon } from '#app/old_components/ui/icon.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Usage, Keyboard, Styling, Parts } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Popover',
}

const component: componentProps = {
	name: 'Popover',
	shortName: 'Popover',
	fileName: 'popover',
	category: 'overlays',
	description: [
		'An overlay positioned relative to a trigger.',
		'There is no built-in way to create popovers in HTML. Popover helps achieve accessible popovers that can be styled as needed.',
	],
	features: [
		'Accessible and keyboard navigable.',
		'Easily styleable, including entry and exit animations.',
		'Focus is moved into the popover on mount, and restored to the trigger element on unmount.',
		'While open, focus is contained within the popover, preventing the user from tabbing outside.',
		'Exposed to assistive technology as a dialog with ARIA.',
		'The popover is automatically labeled by a nested PopoverTitle element.',
		'Content outside the popover is hidden from assistive technologies while it is open.',
		'Esc closes the component automatically.',
		'Automatically flips and adjusts position to avoid overlapping with the edge of the viewport.',
	],
	keyboard: [
		{ key: 'Space', description: 'Opens the popover.' },
		{ key: 'Enter', description: 'Opens the popover.' },
		{ key: 'Tab', description: 'Moves focus to the next focusable element.' },
		{ key: 'Shift + Tab', description: 'Moves focus to the previous focusable element.' },
		{ key: 'Esc', description: 'Closes the popover and moves focus to the PopoverTrigger.' },
	],
	parts: [
		{
			name: 'PopoverTrigger',
			description: 'The PopoverTrigger component does not render any DOM elements. It only passes through its children.',
			props: [
				{ name: 'children *', type: 'ReactNode', default: '', description: 'PopoverTrigger needs at least two children: a <Button> and a <Popover>.' },
				{ name: 'isOpen', type: 'boolean', default: '', description: 'Whether the popover is open by default (controlled).' },
				{ name: 'defaultOpen', type: 'boolean', default: '', description: 'Whether the overlay is open by default (uncontrolled).' },
			],
			events: [{ name: 'onOpenChange', type: '(isOpen: boolean) => void', description: "Handler that is called when the overlay's open state changes." }],
		},
		{
			name: 'Popover',
			description: 'An overlay positioned relative to a trigger.',
			props: [
				{
					name: 'side',
					type: "'center' | 'left' | 'right' | 'top' | 'bottom'",
					default: "'center'",
					description: 'Side of the screen the popover will open from.',
				},
				{
					name: 'offset',
					type: '"number"',
					default: '',
					description: 'Spacing between the popover and the trigger across the main axis.',
				},
				{
					name: 'crossOffset',
					type: '"number"',
					default: '',
					description: 'Spacing between the popover and the trigger across the cross axis.',
				},
				{
					name: 'children',
					type: 'ReactNode | (opts: PopoverRenderProps) => ReactNode',
					default: '',
					description: 'Children of the popover. A function may be provided to access a function to close the popover.',
				},
				{
					name: 'className',
					type: 'string | (values: ButtonRenderProps) => string',
					default: '',
					description: 'The CSS className for the element. A function may be provided to compute the class based on component state.',
				},
				{ name: 'style', type: 'CSSProperties', default: '', description: 'The inline style for the element.' },
			],
			accessibility: [
				{ name: 'id', type: 'string', description: "The element's unique identifier." },
				{ name: 'aria-label', type: 'string', description: 'Defines a string value that labels the current element.' },
				{ name: 'aria-labelledby', type: 'string', description: 'Identifies the element (or elements) that labels the current element.' },
				{ name: 'aria-describedby', type: 'string', description: 'Identifies the element (or elements) that describes the object.' },
				{ name: 'aria-details', type: 'string', description: 'Identifies the element (or elements) that provide a detailed, extended description for the object.' },
			],
		},
		{
			name: 'PopoverTitle',
			description: 'Contains the title of the popover.',
		},
	],
	usage: `<PopoverTrigger>
	<Button variant="ghost" className="border text-muted-400">
		<Icon name="avatar" />
		<VisuallyHidden>Account</VisuallyHidden>
	</Button>
	<Popover>
		{({ close }) => (
			<>
				<VisuallyHidden>
					<PopoverTitle>Account</PopoverTitle>
				</VisuallyHidden>
				<Flex orientation="vertical">
					<Flex orientation="vertical" gap="4" className="w-full border-b border-muted-100 px-5 py-3">
						<Flex gap="8" className="mb-3">
							<Icon name="avatar" className="h-10 w-10 text-muted-300" />
							<Flex orientation="vertical">
								<Span weight="semibold" className="leading-snug">
									John Doe
								</Span>
								<Span size="xs" className="leading-tight text-muted-400">
									john@gmail.com
								</Span>
							</Flex>
						</Flex>
						<Button variant="ghost" className="flex w-full justify-start gap-2 font-normal text-muted-500" autoFocus onPress={close}>
							<Icon name="magnifying-glass" />
							<Span>Search</Span>
						</Button>
						<Button variant="ghost" className="flex w-full justify-start gap-2 font-normal text-muted-500" onPress={close}>
							<Icon name="avatar" />
							<Span>Profile</Span>
						</Button>
						<Button variant="ghost" className="flex w-full justify-start gap-2 font-normal text-muted-500" onPress={close}>
							<Icon name="dashboard" />
							<Span>Feed</Span>
						</Button>
						<Button variant="ghost" className="flex w-full justify-start gap-2 font-normal text-muted-500" onPress={close}>
							<Icon name="bar-chart" />
							<Span>Analytics</Span>
						</Button>
					</Flex>
					<Flex orientation="vertical" gap="4" className="w-full border-b border-muted-100 px-5 py-3">
						<Button variant="ghost" className="flex w-full justify-start gap-2 font-normal text-muted-500" onPress={close}>
							<Icon name="gear" />
							<Span>Settings</Span>
						</Button>
						<Button variant="ghost" className="flex w-full justify-start gap-2 font-normal text-muted-500" onPress={close}>
							<Icon name="question-mark" />
							<Span>Support</Span>
						</Button>
					</Flex>
					<Flex orientation="vertical" gap="4" className="w-full px-5 py-3">
						<Button variant="ghost" className="flex w-full justify-start gap-2 font-normal text-muted-500" onPress={close}>
							<Icon name="exit" />
							<Span>Log out</Span>
						</Button>
					</Flex>
				</Flex>
			</>
		)}
	</Popover>
</PopoverTrigger>`,
	styling: `/* ### Overlays ### */
@layer components {
	/* Popover */
	.popover {
		@apply min-w-[18rem] rounded-xl border bg-background;
	}

	.popover-arrow {
		@apply fill-background stroke-muted-300 stroke-1;
	}
}`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const DialogRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, features, keyboard, parts, usage, shortName, fileName, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Features features={features} />
			<Keyboard keyboard={keyboard} />
			<Parts parts={parts} />
			<P className="mt-4">
				The <Code>{'<PopoverTitle>'}</Code> has the same props as an{' '}
				<Link to="/components/typography/h2">
					<Code>{'<H2>'}</Code>
				</Link>{' '}
				and is mandatory so assistive technology announces the popover correctly.
			</P>
			<P className="mt-4">
				If you want to visually hide the title, wrap it with <Code>{'<VisuallyHidden>'}</Code>.
			</P>
			<Usage usage={usage} shortName={shortName} />
			<PopoverTrigger>
				<Button variant="ghost" className="text-muted-400 border">
					<Icon name="avatar" />
					<VisuallyHidden>Account</VisuallyHidden>
				</Button>
				<Popover>
					{({ close }) => (
						<>
							<VisuallyHidden>
								<PopoverTitle>Account</PopoverTitle>
							</VisuallyHidden>
							<Flex orientation="vertical">
								<Flex orientation="vertical" gap="4" className="border-muted-100 w-full border-b py-3 px-5">
									<Flex gap="8" className="mb-3">
										<Icon name="avatar" className="text-muted-300 h-10 w-10" />
										<Flex orientation="vertical">
											<Span className="text-semibold leading-snug">John Doe</Span>
											<Span className="text-muted-400 text-xs leading-tight">john@gmail.com</Span>
										</Flex>
									</Flex>
									<Button variant="ghost" className="text-muted-500 flex w-full justify-start gap-2 font-normal" autoFocus onPress={close}>
										<Icon name="magnifying-glass" />
										<Span>Search</Span>
									</Button>
									<Button variant="ghost" className="text-muted-500 flex w-full justify-start gap-2 font-normal" onPress={close}>
										<Icon name="avatar" />
										<Span>Profile</Span>
									</Button>
									<Button variant="ghost" className="text-muted-500 flex w-full justify-start gap-2 font-normal" onPress={close}>
										<Icon name="dashboard" />
										<Span>Feed</Span>
									</Button>
									<Button variant="ghost" className="text-muted-500 flex w-full justify-start gap-2 font-normal" onPress={close}>
										<Icon name="bar-chart" />
										<Span>Analytics</Span>
									</Button>
								</Flex>
								<Flex orientation="vertical" gap="4" className="border-muted-100 w-full border-b py-3 px-5">
									<Button variant="ghost" className="text-muted-500 flex w-full justify-start gap-2 font-normal" onPress={close}>
										<Icon name="gear" />
										<Span>Settings</Span>
									</Button>
									<Button variant="ghost" className="text-muted-500 flex w-full justify-start gap-2 font-normal" onPress={close}>
										<Icon name="question-mark" />
										<Span>Support</Span>
									</Button>
								</Flex>
								<Flex orientation="vertical" gap="4" className="w-full py-3 px-5">
									<Button variant="ghost" className="text-muted-500 flex w-full justify-start gap-2 font-normal" onPress={close}>
										<Icon name="exit" />
										<Span>Log out</Span>
									</Button>
								</Flex>
							</Flex>
						</>
					)}
				</Popover>
			</PopoverTrigger>
			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default DialogRoute
